import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/apiClient';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { theme } from '../styles/theme';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import BackButton from '../components/ui/BackButton';
import AlertModal from '../components/ui/AlertModal';
import { useAlert } from '../hooks/useAlert';
import { formatSqft, pricePerSqft } from '../utils/sizeEstimate';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isAuthenticated, isApproved, pricingTier, loading: authLoading } = useAuth();
  const { alertState, showSuccess, showError, closeAlert } = useAlert();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedComponents, setSelectedComponents] = useState([]); // Array of {componentId, variationId (optional), name, price}
  const touchStartRef = useRef(0);
  const touchEndRef = useRef(0);
  const [isMobile, setIsMobile] = useState(false);
  const imageGalleryRef = useRef(null);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(null); // Price with quantity discount applied
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceCalculation, setPriceCalculation] = useState(null); // Full price calculation data including discount info
  const [quantityInput, setQuantityInput] = useState(''); // For manual quantity input
  const [pendingQuantity, setPendingQuantity] = useState(null); // Track if there's a pending quantity change
  const [isAddingToCart, setIsAddingToCart] = useState(false); // Prevent double-click on add to cart
  const [hasPendingPriceUpdate, setHasPendingPriceUpdate] = useState(false); // Track if there's a debounced price calculation waiting

  // Image zoom state
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const [lightboxPan, setLightboxPan] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const pinchStartDistRef = useRef(0);
  const pinchStartZoomRef = useRef(1);
  const lastTapRef = useRef(0);

  // Helper to check if a sale is currently active
  const isSaleActive = (saleDiscountPercent, saleStartDate, saleEndDate) => {
    if (!saleDiscountPercent || !saleEndDate) return false;
    const now = new Date();
    const endDate = new Date(saleEndDate);
    const startDate = saleStartDate ? new Date(saleStartDate) : null;
    
    if (endDate < now) return false; // Sale has ended
    if (startDate && startDate > now) return false; // Sale hasn't started yet
    return true;
  };

  useEffect(() => {
    if (!authLoading) {
      fetchProduct();
    }
  }, [productId, authLoading]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset selected image when subcategory changes
  useEffect(() => {
    setSelectedImage(0);
  }, [selectedSubcategory]);

  // Reset zoom when selected image changes
  useEffect(() => {
    setIsZoomed(false);
    if (lightboxOpen) {
      setLightboxZoom(1);
      setLightboxPan({ x: 0, y: 0 });
    }
  }, [selectedImage]);

  // Lightbox keyboard shortcuts
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          setLightboxOpen(false);
          setLightboxZoom(1);
          setLightboxPan({ x: 0, y: 0 });
          document.body.style.overflow = '';
          break;
        case 'ArrowLeft':
          setSelectedImage(prev => prev > 0 ? prev - 1 : prev);
          break;
        case 'ArrowRight':
          setSelectedImage(prev => {
            const imgs = selectedSubcategory?.images || product?.images || [];
            return prev < imgs.length - 1 ? prev + 1 : prev;
          });
          break;
        case '+':
        case '=':
          setLightboxZoom(prev => Math.min(prev + 0.5, 5));
          break;
        case '-':
          setLightboxZoom(prev => {
            const next = Math.max(prev - 0.5, 1);
            if (next === 1) setLightboxPan({ x: 0, y: 0 });
            return next;
          });
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, selectedSubcategory, product]);

  // Calculate price with quantity discounts when quantity or variant changes
  useEffect(() => {
    const calculatePrice = async () => {
      if (!product || !isApproved) {
        setCalculatedPrice(null);
        setHasPendingPriceUpdate(false);
        return;
      }

      // Only calculate discounted price if product has variants (subcategories)
      const hasSubcategories = product.subcategories && product.subcategories.length > 0;
      
      // If product has variants, only calculate when a variant is selected
      if (hasSubcategories && !selectedSubcategory) {
        setCalculatedPrice(null);
        setHasPendingPriceUpdate(false);
        return;
      }

      // Use pendingQuantity if available, otherwise use quantity
      const quantityToCalculate = pendingQuantity || quantity;

      setPriceLoading(true);
      setHasPendingPriceUpdate(false); // Clear pending flag as we're now calculating
      try {
        const priceData = await api.products.calculatePrice(
          product.id,
          quantityToCalculate,
          selectedSubcategory?.id || null
        );
        setCalculatedPrice(priceData.unit_price);
        setPriceCalculation(priceData);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error calculating price:', err);
        }
        // Fallback to base price without discount
        setCalculatedPrice(null);
        setPriceCalculation(null);
      } finally {
        setPriceLoading(false);
      }
    };

    // Set pending flag immediately when dependencies change
    setHasPendingPriceUpdate(true);

    // Debounce the price calculation when pendingQuantity changes
    // This prevents rapid API calls while typing
    const timeoutId = setTimeout(() => {
      calculatePrice();
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [product, quantity, pendingQuantity, selectedSubcategory, isApproved]);

  const handleQuantityInputChange = (value) => {
    // Allow only numbers or empty string
    const numValue = value.replace(/[^0-9]/g, '');
    setQuantityInput(numValue);
    if (numValue && parseInt(numValue, 10) > 0) {
      setPendingQuantity(parseInt(numValue, 10));
    } else {
      setPendingQuantity(null);
    }
  };

  const handleCalculatePrice = () => {
    if (pendingQuantity && pendingQuantity >= 1) {
      const maxStock = calculateMaxStock();
      const newQuantity = Math.min(pendingQuantity, maxStock);
      setQuantity(newQuantity);
      setQuantityInput(''); // Clear input after update
      setPendingQuantity(null);
    }
  };

  // Check if we have a valid quantity for add to cart
  const hasValidQuantity = quantityInput === '' ? quantity >= 1 : (pendingQuantity && pendingQuantity >= 1);

  // Calculate maximum available stock based on product variant and component variants
  const calculateMaxStock = () => {
    if (!product) return 0;
    
    // Start with the product/variant stock
    const hasSubcategories = product.subcategories && product.subcategories.length > 0;
    let maxStock = hasSubcategories 
      ? (selectedSubcategory?.stock_qty || 0) 
      : (product.stock_qty || 0);
    
    // If there are selected components with variations, find the minimum stock
    selectedComponents.forEach(selectedComp => {
      if (selectedComp.variationId) {
        const component = product.components?.find(c => c.id === selectedComp.componentId);
        if (component && component.variations) {
          const variation = component.variations.find(v => v.id === selectedComp.variationId);
          if (variation && variation.stock_qty != null) {
            // The maximum we can add is limited by the component with the lowest stock
            maxStock = Math.min(maxStock, variation.stock_qty);
          }
        }
      }
    });
    
    return maxStock;
  };

  // Adjust quantity when available stock changes due to variant/component selection
  useEffect(() => {
    const maxStock = calculateMaxStock();
    if (quantity > maxStock && maxStock > 0) {
      setQuantity(maxStock);
    }
  }, [selectedSubcategory, selectedComponents]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const data = await api.products.getDetail(productId);
      setProduct(data);
    } catch (err) {
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyWhenInStock = async () => {
    if (!isAuthenticated) {
      showError('Please log in to get stock notifications', 'Login Required');
      return;
    }

    try {
      setNotifyLoading(true);
      
      // Determine what to track based on current selection
      const notificationData = {
        product_id: productId,
      };

      // If a variant is selected, track that specific variant
      if (selectedSubcategory) {
        notificationData.variant_id = selectedSubcategory.id;
      }

      // If a component variation is out of stock, track that
      // Find the first out-of-stock component variation
      for (const selectedComp of selectedComponents) {
        if (selectedComp.variationId) {
          const component = product.components?.find(c => c.id === selectedComp.componentId);
          if (component && component.variations) {
            const variation = component.variations.find(v => v.id === selectedComp.variationId);
            if (variation && variation.stock_qty === 0) {
              notificationData.component_variation_id = variation.id;
              break;
            }
          }
        }
      }

      await api.stockNotifications.create(notificationData);
      
      let itemName = product.name;
      if (selectedSubcategory) {
        itemName += ` - ${selectedSubcategory.name}`;
      }
      
      showSuccess(
        `You'll be notified when "${itemName}" is back in stock!`,
        'Notification Set'
      );
    } catch (err) {
      console.error('Error creating stock notification:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to set notification';
      showError(errorMsg, 'Notification Failed');
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleAddToCart = async () => {
    // Prevent double-click/double-tap or adding while price is calculating
    if (isAddingToCart || priceLoading || hasPendingPriceUpdate) {
      return;
    }

    if (product && (product.price_visible || isApproved)) {
      setIsAddingToCart(true);
      
      try {
        // Resolve the quantity to add - prioritize pending input if valid
        let quantityToAdd = quantity;
        if (pendingQuantity && pendingQuantity > 0) {
          quantityToAdd = pendingQuantity;
        }

        // Check if product has subcategories and one needs to be selected
        const hasSubcategories = product.subcategories && product.subcategories.length > 0;
        if (hasSubcategories && !selectedSubcategory) {
          alert('Please select a variant before adding to cart.');
          return;
        }
        
        // Check if any required components are not selected
        const requiredComponents = product.components?.filter(c => c.is_required && c.is_active) || [];
        const missingRequired = requiredComponents.filter(
          req => !selectedComponents.find(sel => sel.componentId === req.id)
        );
        
        if (missingRequired.length > 0) {
          alert(`Please select the following required components: ${missingRequired.map(c => c.name).join(', ')}`);
          return;
        }
        
        // Check if all selected components with variations have a variation chosen
        for (const selectedComp of selectedComponents) {
          const component = product.components.find(c => c.id === selectedComp.componentId);
          if (component && component.variations && component.variations.length > 0) {
            if (!selectedComp.variationId) {
              alert(`Please select a variation for "${component.name}".`);
              return;
            }
            // Guard against null prices — happens if product was fetched before auth was ready
            if (selectedComp.price == null || isNaN(parseFloat(selectedComp.price))) {
              alert('Product prices are still loading. Please wait a moment and try again.');
              return;
            }
          }
        }
        
        // Determine the correct price to use
        let basePrice = currentPrice;
        
        // If the quantity to add is different from the currently calculated quantity,
        // we need to recalculate the price to ensure quantity discounts are applied correctly
        if (quantityToAdd !== quantity) {
          try {
            // Show loading state while we fetch the correct price
            setPriceLoading(true);
            
            const priceData = await api.products.calculatePrice(
              product.id,
              quantityToAdd,
              selectedSubcategory?.id || null
            );
            
            if (priceData && priceData.unit_price !== undefined) {
              basePrice = priceData.unit_price;
              // Update state to match the new calculation
              setCalculatedPrice(priceData.unit_price);
              setPriceCalculation(priceData);
            } else {
              // Fallback if API returns incomplete data
              basePrice = getCurrentPriceWithSale();
            }
          } catch (err) {
            console.error('Error recalculating price for cart:', err);
            // If calculation fails, fall back to the safe price (with sale but NO quantity discount)
            // This ensures we don't accidentally keep a quantity discount if the user lowered quantity
            basePrice = getCurrentPriceWithSale();
          } finally {
            setPriceLoading(false);
          }
        }
        
        // Prevent adding products with invalid price (0.00)
        if (!basePrice || parseFloat(basePrice) <= 0) {
          alert('Unable to add product to cart. Please refresh the page and try again.');
          return;
        }
        
        // Calculate base price without any discounts (no sale, no quantity discount) for comparison
        let basePriceWithoutDiscount = parseFloat(baseTierPrice);
        
        // Add component prices
        const componentsTotalPrice = selectedComponents.reduce((sum, sc) => sum + (parseFloat(sc.price) || 0), 0);
        const totalPrice = parseFloat(basePrice) + componentsTotalPrice;
        const totalBasePriceWithoutDiscount = parseFloat(basePriceWithoutDiscount) + componentsTotalPrice;
        
        // Format selected components for cart
        const formattedComponents = selectedComponents.map(sc => ({
          id: sc.componentId,
          name: sc.name,
          price: sc.price,
          variation_id: sc.variationId || null,
          variation_name: sc.variationName || null,
        }));
        
        // Generate a unique cart ID that includes components
        // This ensures products with different components are treated as separate cart items
        const generateCartId = (productId, subcategoryId, components) => {
          let cartId = productId;
          if (subcategoryId) {
            cartId += `_${subcategoryId}`;
          }
          if (components && components.length > 0) {
            // Sort components to ensure consistent ordering
            const sortedComponents = [...components].sort((a, b) => a.id.localeCompare(b.id));
            const componentSignature = sortedComponents
              .map(c => `${c.id}${c.variation_id ? `:${c.variation_id}` : ''}`)
              .join('|');
            cartId += `_comp:${componentSignature}`;
          }
          return cartId;
        };
        
        if (selectedSubcategory) {
          const uniqueCartId = generateCartId(product.id, selectedSubcategory.id, formattedComponents);
          const productWithSubcategory = {
            ...product,
            subcategory: selectedSubcategory.id, // Pass ID as string, not the entire object
            subcategoryName: selectedSubcategory.name, // Pass name separately
            // Use combined ID for cart tracking (now includes components)
            cartId: uniqueCartId,
            // Override images and stock with subcategory specific values
            images: selectedSubcategory.images,
            stock_qty: selectedSubcategory.stock_qty,
            variantName: selectedSubcategory.name,
            // Use variant price based on user's tier
            price: totalPrice,
            base_price_without_discount: totalBasePriceWithoutDiscount, // Store base price for discount display
            selected_components: formattedComponents,
          };
          try {
            await addItem(productWithSubcategory, quantityToAdd);
          } catch (err) {
            alert(err.message || 'This item couldn’t be added to your cart.');
            return;
          }
        } else {
          const uniqueCartId = generateCartId(product.id, null, formattedComponents);
          const productWithComponents = {
            ...product,
            cartId: uniqueCartId, // Add unique cart ID for products with components
            price: totalPrice,
            base_price_without_discount: totalBasePriceWithoutDiscount, // Store base price for discount display
            selected_components: formattedComponents,
          };
          try {
            await addItem(productWithComponents, quantityToAdd);
          } catch (err) {
            alert(err.message || 'This item couldn’t be added to your cart.');
            return;
          }
        }
        
        // Reset selected components after adding to cart
        setSelectedComponents([]);
        
        // Always reset quantity input to show default quantity in the field
        setQuantityInput('');
        setPendingQuantity(null);
        
        // If we used a pending quantity, update the state quantity
        if (quantityToAdd !== quantity) {
          setQuantity(quantityToAdd);
        }
      } finally {
        // Always reset the adding state, even if there was an error or early return
        setIsAddingToCart(false);
      }
    }
  };

  // Touch handlers for swipe on mobile
  const handleTouchStart = (e) => {
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    
    const distance = touchStartRef.current - touchEndRef.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    const displayImages = selectedSubcategory?.images || product?.images || [];
    
    if (isLeftSwipe && selectedImage < displayImages.length - 1) {
      setSelectedImage(selectedImage + 1);
    }
    
    if (isRightSwipe && selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
    
    touchStartRef.current = 0;
    touchEndRef.current = 0;
  };

  // Desktop hover zoom handlers
  const handleImageMouseMove = (e) => {
    if (isMobile) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  const handleImageMouseEnter = () => {
    if (!isMobile) setIsZoomed(true);
  };

  const handleImageMouseLeave = () => {
    setIsZoomed(false);
  };

  // Lightbox handlers
  const openLightbox = () => {
    setLightboxOpen(true);
    setLightboxZoom(1);
    setLightboxPan({ x: 0, y: 0 });
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxZoom(1);
    setLightboxPan({ x: 0, y: 0 });
    document.body.style.overflow = '';
  };

  const handleLightboxWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.3 : 0.3;
    setLightboxZoom(prev => {
      const next = Math.min(Math.max(prev + delta, 1), 5);
      if (next === 1) setLightboxPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleLightboxPointerDown = (e) => {
    if (lightboxZoom <= 1) return;
    isPanningRef.current = true;
    panStartRef.current = { x: e.clientX - lightboxPan.x, y: e.clientY - lightboxPan.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleLightboxPointerMove = (e) => {
    if (!isPanningRef.current) return;
    setLightboxPan({
      x: e.clientX - panStartRef.current.x,
      y: e.clientY - panStartRef.current.y,
    });
  };

  const handleLightboxPointerUp = () => {
    isPanningRef.current = false;
  };

  const handleLightboxTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      pinchStartDistRef.current = dist;
      pinchStartZoomRef.current = lightboxZoom;
    } else if (e.touches.length === 1 && lightboxZoom > 1) {
      isPanningRef.current = true;
      panStartRef.current = {
        x: e.touches[0].clientX - lightboxPan.x,
        y: e.touches[0].clientY - lightboxPan.y,
      };
    }

    // Double-tap detection
    if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        if (lightboxZoom > 1) {
          setLightboxZoom(1);
          setLightboxPan({ x: 0, y: 0 });
        } else {
          setLightboxZoom(2.5);
        }
      }
      lastTapRef.current = now;
    }
  };

  const handleLightboxTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scale = dist / pinchStartDistRef.current;
      const newZoom = Math.min(Math.max(pinchStartZoomRef.current * scale, 1), 5);
      setLightboxZoom(newZoom);
      if (newZoom === 1) setLightboxPan({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && isPanningRef.current) {
      setLightboxPan({
        x: e.touches[0].clientX - panStartRef.current.x,
        y: e.touches[0].clientY - panStartRef.current.y,
      });
    }
  };

  const handleLightboxTouchEnd = () => {
    isPanningRef.current = false;
    pinchStartDistRef.current = 0;
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.background,
        padding: theme.spacing.lg,
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: `4px solid ${theme.colors.border}`,
          borderTop: `4px solid ${theme.colors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: theme.spacing.lg,
        }} />
        <p style={{ 
          color: theme.colors.textSecondary,
          fontSize: theme.typography.fontSize.lg,
          fontWeight: theme.typography.fontWeight.medium,
        }}>
          Loading product...
        </p>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.background,
        padding: theme.spacing.lg,
        textAlign: 'center',
      }}>
        <h2 style={{ 
          color: theme.colors.text, 
          fontSize: theme.typography.fontSize['3xl'], 
          marginBottom: theme.spacing.md,
          fontFamily: theme.typography.fontFamily.primary,
          fontWeight: theme.typography.fontWeight.bold,
        }}>
          Product Not Found
        </h2>
        <p style={{
          color: theme.colors.textSecondary,
          fontSize: theme.typography.fontSize.lg,
          marginBottom: theme.spacing.xl,
        }}>
          The product you're looking for doesn't exist or has been removed.
        </p>
        <BackButton 
          to="/products" 
          label="Back to Products"
          variant="solid"
          size="lg"
        />
      </div>
    );
  }

  // Get display images based on selection
  const displayImages = selectedSubcategory?.images || product.images || [];
  const hasSubcategories = product.subcategories && product.subcategories.length > 0;
  const availableStock = calculateMaxStock();
  
  // Check if all required components are selected
  const requiredComponents = product.components?.filter(c => c.is_required && c.is_active) || [];
  const allRequiredComponentsSelected = requiredComponents.every(
    req => selectedComponents.find(sel => sel.componentId === req.id)
  );
  
  // Check if all selected components with variations have a variation chosen
  const allComponentVariationsSelected = selectedComponents.every(selectedComp => {
    const component = product.components?.find(c => c.id === selectedComp.componentId);
    if (component && component.variations && component.variations.length > 0) {
      return selectedComp.variationId != null;
    }
    return true;
  });
  
  // Check if all conditions are met to show the add to cart section
  // Products marked inactive in the admin can still be opened from old links,
  // but can't be bought.
  const isDiscontinued = product?.is_active === false;

  const canShowAddToCart = !isDiscontinued
    && (hasSubcategories 
    ? selectedSubcategory && availableStock > 0 
    : availableStock > 0) 
    && allRequiredComponentsSelected 
    && allComponentVariationsSelected;
  
  // Button should be disabled if price is loading or no valid quantity
  const isAddToCartDisabled = priceLoading || !hasValidQuantity || isAddingToCart || hasPendingPriceUpdate;
  
  // Helper to check if sale applies to current user's tier
  const isSaleApplicableToTier = (saleApplicableTiers) => {
    if (!saleApplicableTiers || saleApplicableTiers.length === 0) {
      return true; // If no tiers specified, sale applies to all
    }
    return saleApplicableTiers.includes(pricingTier);
  };

  // Helper to get base tier price (before sales or quantity discounts)
  // This calculates what the price WOULD be without any discounts
  const getBaseTierPrice = () => {
    if (!selectedSubcategory) {
      // For products without variants, we need to calculate the base tier price
      // The backend returns product.price which may already have sale applied
      // We need to reverse-calculate if there's an active sale
      const productSaleActive = isSaleActive(
        product.sale_discount_percent,
        product.sale_start_date,
        product.sale_end_date
      );
      
      if (productSaleActive && isSaleApplicableToTier(product.sale_applicable_tiers)) {
        // Reverse calculate: if price = base * (1 - discount%), then base = price / (1 - discount%)
        const discountMultiplier = 1 - (product.sale_discount_percent / 100);
        return parseFloat(product.price) / discountMultiplier;
      }
      
      return parseFloat(product.price);
    }
    
    // For variants with tier_prices, get the appropriate tier price
    if (selectedSubcategory.tier_prices) {
      const tierPrices = selectedSubcategory.tier_prices;
      
      // Get price based on user's tier, with fallback logic
      if (pricingTier === 'volume' && tierPrices.volume) {
        return parseFloat(tierPrices.volume);
      } else if (pricingTier === 'wholesale' && tierPrices.wholesale) {
        return parseFloat(tierPrices.wholesale);
      } else if (tierPrices.retail) {
        return parseFloat(tierPrices.retail);
      }
    }
    
    // If variant has calculated_price but no tier_prices, reverse-calculate from sale
    if (selectedSubcategory.calculated_price) {
      const variantSaleActive = isSaleActive(
        selectedSubcategory.sale_discount_percent,
        selectedSubcategory.sale_start_date,
        selectedSubcategory.sale_end_date
      );
      
      if (variantSaleActive && isSaleApplicableToTier(selectedSubcategory.sale_applicable_tiers)) {
        const discountMultiplier = 1 - (selectedSubcategory.sale_discount_percent / 100);
        return parseFloat(selectedSubcategory.calculated_price) / discountMultiplier;
      }
      
      return parseFloat(selectedSubcategory.calculated_price);
    }
    
    // Fall back to main product price
    return parseFloat(product.price);
  };

  // Get the current display price (with sales but before quantity discounts)
  const getCurrentPriceWithSale = () => {
    if (!selectedSubcategory) {
      // Product price from backend already has sale applied if applicable
      return parseFloat(product.price);
    }
    
    // For variants, use calculated_price if available (backend already applied sale)
    if (selectedSubcategory.calculated_price !== undefined && selectedSubcategory.calculated_price !== null) {
      return parseFloat(selectedSubcategory.calculated_price);
    }
    
    // If no calculated_price, calculate from tier_prices with sale
    const baseTierPrice = getBaseTierPrice();
    
    const variantSaleActive = isSaleActive(
      selectedSubcategory.sale_discount_percent,
      selectedSubcategory.sale_start_date,
      selectedSubcategory.sale_end_date
    );
    
    if (variantSaleActive && isSaleApplicableToTier(selectedSubcategory.sale_applicable_tiers)) {
      const discount = selectedSubcategory.sale_discount_percent / 100;
      return baseTierPrice * (1 - discount);
    }
    
    return baseTierPrice;
  };

  // Get the current price - use calculated price with quantity discount if available
  const getCurrentPrice = () => {
    // If we have a calculated price with quantity discount from API, use that
    if (calculatedPrice !== null && calculatedPrice !== undefined) {
      return calculatedPrice;
    }
    
    // Otherwise, use price with sale applied
    return getCurrentPriceWithSale();
  };
  
  const currentPrice = getCurrentPrice();
  const baseTierPrice = getBaseTierPrice();
  const priceWithSale = getCurrentPriceWithSale();

  // Options whose pieces vary in size (hides) are priced for an average piece;
  // the order is re-priced from the measured size before the customer is charged.
  const approxSqft = selectedSubcategory?.approx_sqft || null;
  const ratePerSqft = approxSqft ? pricePerSqft(currentPrice, approxSqft) : null;

  // Check if there's an active sale for display purposes
  const hasActiveSale = () => {
    if (selectedSubcategory) {
      const variantSaleActive = isSaleActive(
        selectedSubcategory.sale_discount_percent,
        selectedSubcategory.sale_start_date,
        selectedSubcategory.sale_end_date
      );
      if (variantSaleActive && isSaleApplicableToTier(selectedSubcategory.sale_applicable_tiers)) {
        return { type: 'variant', percent: selectedSubcategory.sale_discount_percent };
      }
    }
    
    const productSaleActive = isSaleActive(
      product.sale_discount_percent,
      product.sale_start_date,
      product.sale_end_date
    );
    
    if (productSaleActive && isSaleApplicableToTier(product.sale_applicable_tiers)) {
      return { type: 'product', percent: product.sale_discount_percent };
    }
    
    return null;
  };
  
  const activeSale = hasActiveSale();

  return (
    <div style={{ backgroundColor: theme.colors.background, minHeight: '100vh' }}>
      <AlertModal
        isOpen={alertState.isOpen}
        onClose={closeAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
      />
      
      {/* Header with Back Button */}
      <div className="product-header" style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: theme.colors.surface,
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: `${theme.spacing.md} ${theme.spacing.lg}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.md,
        }}>
          <BackButton variant="default" size="md" />
          <span style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
            display: 'none',
          }} className="breadcrumb">
            {product.name}
          </span>
        </div>
      </div>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? `${theme.spacing.lg} ${theme.spacing.md}` : `${theme.spacing.xl} ${theme.spacing.lg}`,
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}>
        <div className="product-detail-grid" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? theme.spacing.lg : theme.spacing.xxl,
          alignItems: 'start',
          width: '100%',
        }}>
          {/* Image Gallery - Left Side */}
          <div className="product-images-section" style={{
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
          }}>
            {displayImages && displayImages.length > 0 ? (
              <div style={{ 
                position: isMobile ? 'relative' : 'sticky', 
                top: isMobile ? 'auto' : '100px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: theme.spacing.md,
                alignItems: 'flex-start',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
              }}>
                
                {/* Desktop Thumbnails - Left Side */}
                {!isMobile && displayImages.length > 1 && (
                  <div className="desktop-thumbnails" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing.sm,
                    width: '80px',
                    minWidth: '80px',
                    maxHeight: '500px',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    paddingRight: '4px'
                  }}>
                    {displayImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        onMouseEnter={() => setSelectedImage(idx)}
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          border: `2px solid ${selectedImage === idx ? theme.colors.primary : 'transparent'}`,
                          borderRadius: theme.borderRadius.md,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          padding: '2px',
                          backgroundColor: theme.colors.surface,
                          transition: 'all 0.2s ease',
                          opacity: selectedImage === idx ? 1 : 0.6,
                          flexShrink: 0
                        }}
                      >
                        <img
                          src={img}
                          alt={`View ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: theme.borderRadius.sm,
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* Main Image Area */}
                <div style={{ 
                  flex: 1, 
                  width: '100%', 
                  maxWidth: '100%',
                  minWidth: 0,
                  position: 'relative' 
                }}>
                  <div 
                    ref={imageGalleryRef}
                    className="main-image-container"
                    style={{ 
                      position: 'relative',
                      backgroundColor: theme.colors.surface,
                      borderRadius: theme.borderRadius.xl,
                      overflow: 'hidden',
                      marginBottom: isMobile ? theme.spacing.lg : 0,
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      aspectRatio: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px solid ${theme.colors.border}`,
                      width: '100%',
                      maxWidth: '100%',
                      cursor: isZoomed ? 'zoom-in' : 'pointer',
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseMove={handleImageMouseMove}
                    onMouseEnter={handleImageMouseEnter}
                    onMouseLeave={handleImageMouseLeave}
                    onClick={openLightbox}
                  >
                    <img 
                      src={displayImages[selectedImage]} 
                      alt={selectedSubcategory ? `${product.name} - ${selectedSubcategory.name}` : product.name}
                      className="product-main-image"
                      draggable={false}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: isZoomed ? 0 : (isMobile ? theme.spacing.md : theme.spacing.xl),
                        transition: isZoomed ? 'none' : 'opacity 0.3s ease, padding 0.2s ease',
                        maxWidth: '100%',
                        transform: isZoomed ? 'scale(2.5)' : 'scale(1)',
                        transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                        pointerEvents: 'none',
                      }}
                    />

                    {/* Zoom hint icon */}
                    {!isMobile && !isZoomed && (
                      <div className="zoom-hint" style={{
                        position: 'absolute',
                        top: theme.spacing.md,
                        right: theme.spacing.md,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        zIndex: 5,
                        opacity: 0.7,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none',
                      }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          <line x1="11" y1="8" x2="11" y2="14"></line>
                          <line x1="8" y1="11" x2="14" y2="11"></line>
                        </svg>
                      </div>
                    )}
                    
                    {/* Image Counter / Dots for Mobile */}
                    {displayImages.length > 1 && (
                      <div className="mobile-dots" style={{
                        position: 'absolute',
                        bottom: theme.spacing.lg,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '6px',
                        zIndex: 10,
                        padding: '8px 12px',
                        backgroundColor: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(4px)',
                        borderRadius: '20px',
                      }}>
                        {displayImages.map((_, idx) => (
                          <div 
                            key={idx}
                            style={{
                              width: selectedImage === idx ? '20px' : '6px',
                              height: '6px',
                              borderRadius: '10px',
                              backgroundColor: selectedImage === idx ? theme.colors.primary : theme.colors.textSecondary,
                              opacity: selectedImage === idx ? 1 : 0.3,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {/* Navigation Arrows for Desktop Main Image */}
                    {!isMobile && displayImages.length > 1 && (
                      <>
                        <button
                          onClick={() => setSelectedImage(prev => prev > 0 ? prev - 1 : displayImages.length - 1)}
                          className="nav-arrow nav-arrow-left"
                          style={{
                            position: 'absolute',
                            left: theme.spacing.md,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: theme.colors.text,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            opacity: 0, 
                            zIndex: 5,
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                        </button>
                        <button
                          onClick={() => setSelectedImage(prev => prev < displayImages.length - 1 ? prev + 1 : 0)}
                          className="nav-arrow nav-arrow-right"
                          style={{
                            position: 'absolute',
                            right: theme.spacing.md,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: theme.colors.text,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            opacity: 0,
                            zIndex: 5,
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Mobile Thumbnail Gallery (Below) */}
                  {isMobile && displayImages.length > 1 && (
                    <div style={{
                      display: 'flex',
                      gap: theme.spacing.sm,
                      overflowX: 'auto',
                      paddingBottom: '4px',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      WebkitOverflowScrolling: 'touch',
                      width: '100%',
                      maxWidth: '100%',
                    }} className="thumbnail-gallery mobile-scroll-hide">
                      <style>{`
                        .mobile-scroll-hide::-webkit-scrollbar {
                          display: none;
                        }
                      `}</style>
                      {displayImages.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImage(idx)}
                          style={{
                            minWidth: 'calc(20% - 4px)',
                            width: 'calc(20% - 4px)',
                            aspectRatio: '1',
                            border: `2px solid ${selectedImage === idx ? theme.colors.primary : 'transparent'}`,
                            borderRadius: theme.borderRadius.lg,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            backgroundColor: theme.colors.surface,
                            padding: theme.spacing.xs,
                            opacity: selectedImage === idx ? 1 : 0.7,
                            flexShrink: 0,
                            boxSizing: 'border-box',
                          }}
                        >
                          <img
                            src={img}
                            alt={`View ${idx + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              borderRadius: theme.borderRadius.sm,
                              maxWidth: '100%',
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{
                backgroundColor: theme.colors.surface,
                borderRadius: theme.borderRadius.xl,
                aspectRatio: '1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing.lg,
                border: `1px solid ${theme.colors.border}`,
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  backgroundColor: theme.colors.backgroundAlt,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.textSecondary
                }}>
                   <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                </div>
                <p style={{ 
                  fontSize: theme.typography.fontSize.md,
                  color: theme.colors.textSecondary,
                  fontWeight: theme.typography.fontWeight.medium,
                }}>
                  No Image Available
                </p>
              </div>
            )}
          </div>

          {/* Product Info - Right Side */}
          <div className="product-info-section" style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xl }}>
            {/* Header: Title, Price, SKU */}
            <div>
              {product.categories && product.categories.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: theme.spacing.sm, flexWrap: 'wrap' }}>
                  {product.categories.map(cat => (
                    <span key={cat.id} style={{ 
                      fontSize: '0.75rem', 
                      color: theme.colors.primary, 
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {cat.name}
                    </span>
                  ))}
                </div>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, flexWrap: 'wrap' }}>
                <h1 style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                  fontWeight: 800,
                  color: theme.colors.text,
                  marginBottom: 0,
                  lineHeight: '1.2',
                }}>
                  {product.name}
                </h1>
                {isAuthenticated && isSaleActive(product.sale_discount_percent, product.sale_start_date, product.sale_end_date) 
                  && isSaleApplicableToTier(product.sale_applicable_tiers) && (
                  <span style={{
                    padding: '0.4rem 0.8rem',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    borderRadius: theme.borderRadius.md,
                    fontSize: '0.9rem',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                  }}>
                    {product.sale_discount_percent}% OFF
                  </span>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: theme.spacing.md, 
                color: theme.colors.textSecondary, 
                fontSize: '0.9rem',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontFamily: 'monospace' }}>SKU: {product.sku}</span>
                
                {/* Stock Status Logic */}
                {isDiscontinued ? (
                  <span style={{ color: theme.colors.error, fontWeight: 600 }}>No longer available</span>
                ) : hasSubcategories ? (
                  selectedSubcategory && (selectedSubcategory.stock_qty === 0 || selectedSubcategory.stock_qty < 10) && (
                    <span style={{
                      color: selectedSubcategory.stock_qty === 0 ? theme.colors.error : theme.colors.cta,
                      fontWeight: 600,
                    }}>
                      {selectedSubcategory.stock_qty === 0 ? 'Out of stock' : 'Low stock'}
                    </span>
                  )
                ) : (
                  (product.stock_qty === 0 || product.stock_qty < 10) && (
                    <span style={{
                      color: product.stock_qty === 0 ? theme.colors.error : theme.colors.cta,
                      fontWeight: 600,
                    }}>
                      {product.stock_qty === 0 ? 'Out of stock' : 'Low stock'}
                    </span>
                  )
                )}
              </div>

              {/* Price Display */}
              <div style={{ marginTop: theme.spacing.lg }}>
                {hasSubcategories && !selectedSubcategory ? (
                  <div style={{
                    padding: theme.spacing.md,
                    backgroundColor: theme.colors.surfaceAlt,
                    borderRadius: theme.borderRadius.md,
                    color: theme.colors.textSecondary,
                    display: 'inline-block',
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                  }}>
                    Please select an option to see pricing
                  </div>
                ) : product.price_visible || (isAuthenticated && isApproved) ? (
                  <div>
                    {/* Show original price with strikethrough if on sale */}
                    {activeSale && (
                      <p style={{
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: theme.colors.textSecondary,
                        margin: 0,
                        textDecoration: 'line-through',
                        marginBottom: '0.25rem',
                      }}>
                        ${(Number(baseTierPrice) || 0).toFixed(2)}
                      </p>
                    )}
                    
                    {/* Current price (with sale applied if active) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <p style={{
                        fontSize: '2rem',
                        fontWeight: 800,
                        color: activeSale ? '#ef4444' : theme.colors.primary,
                        margin: 0,
                      }}>
                        ${(Number(currentPrice) || 0).toFixed(2)}
                        {priceLoading && (
                          <span style={{ 
                            fontSize: '0.875rem', 
                            marginLeft: '0.5rem',
                            color: theme.colors.textSecondary 
                          }}>
                            ...
                          </span>
                        )}
                      </p>
                      
                      {/* Sale badge next to price */}
                      {activeSale && (
                        <span style={{
                          padding: '0.4rem 0.8rem',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          borderRadius: theme.borderRadius.md,
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                        }}>
                          {activeSale.percent}% OFF
                        </span>
                      )}

                      {approxSqft && (
                        <span style={{ fontSize: '0.95rem', color: theme.colors.textSecondary, fontWeight: 600 }}>
                          estimated, per piece
                        </span>
                      )}
                    </div>

                    {/* Pieces that vary in size: explain the estimate */}
                    {approxSqft && (
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.75rem 1rem',
                        backgroundColor: '#FFFBEB',
                        border: '1px solid #FCD34D',
                        borderRadius: theme.borderRadius.md,
                        color: '#78350F',
                        fontSize: '0.9rem',
                        lineHeight: 1.5,
                        maxWidth: '34rem',
                      }}>
                        <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Each piece is a different size</strong>
                        Pieces average about {formatSqft(approxSqft)} sq ft, and the price shown is for an average piece
                        {ratePerSqft ? ` ($${ratePerSqft.toFixed(2)} per sq ft)` : ''}. We’ll measure the pieces we pull for your order and adjust your price to their actual size, so it may be higher or lower. You won’t be charged until then.
                      </div>
                    )}
                    
                    {/* Sale info message */}
                    {activeSale && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#fee2e2',
                          border: '1px solid #fca5a5',
                          borderRadius: theme.borderRadius.sm,
                          fontSize: '0.875rem',
                        }}>
                          <span style={{ color: '#dc2626', fontWeight: 600 }}>
                            {activeSale.type === 'variant' 
                              ? `Sale: ${activeSale.percent}% off this variant` 
                              : `Sale: ${activeSale.percent}% off`}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Show next quantity discount available */}
                    {!activeSale && priceCalculation && priceCalculation.next_discount_min_qty && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#dbeafe',
                          border: '1px solid #93c5fd',
                          borderRadius: theme.borderRadius.sm,
                          fontSize: '0.875rem',
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                          </svg>
                          <span style={{ color: '#2563eb', fontWeight: 600 }}>
                            Buy {priceCalculation.next_discount_min_qty} or more {priceCalculation.next_discount_percent ? `for a ${priceCalculation.next_discount_percent}% discount` : 'for a discount'}
                          </span>
                        </div>
                      </div>
                    )}
                    {calculatedPrice !== null && calculatedPrice !== undefined && (
                      (() => {
                        const hasSubcategories = product.subcategories && product.subcategories.length > 0;
                        
                        // For products with variants, only show discount if a variant is selected
                        if (hasSubcategories && !selectedSubcategory) {
                          return null;
                        }
                        
                        // Compare calculated price against the sale price (not base tier price)
                        // This way quantity discounts are shown relative to the already-discounted sale price
                        const priceBeforeQuantityDiscount = Number(priceWithSale) || 0;
                        const calculatedPriceNum = Number(calculatedPrice) || 0;
                        
                        // Validate that we have valid prices before showing discount
                        if (priceBeforeQuantityDiscount <= 0 || calculatedPriceNum < 0) {
                          return null;
                        }
                        
                        // Check if quantity discount is applied (beyond any sale discount)
                        const hasQuantityDiscount = Math.abs(calculatedPriceNum - priceBeforeQuantityDiscount) > 0.01;
                        
                        if (hasQuantityDiscount && priceCalculation && priceCalculation.applied_discount_percent > 0) {
                          const savingsPercent = priceCalculation.applied_discount_percent || 
                            ((priceBeforeQuantityDiscount - calculatedPriceNum) / priceBeforeQuantityDiscount * 100).toFixed(0);
                          
                          // Additional validation for the calculated percentage
                          if (!isFinite(savingsPercent) || savingsPercent < 0 || savingsPercent <= 0) {
                            return null;
                          }
                          
                          return (
                            <div style={{ marginTop: '0.5rem' }}>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 0.75rem',
                                backgroundColor: '#dcfce7',
                                border: '1px solid #86efac',
                                borderRadius: theme.borderRadius.sm,
                                fontSize: '0.875rem',
                              }}>
                                <span style={{ color: '#16a34a', fontWeight: 600 }}>
                                  Quantity Discount ({priceCalculation.applied_discount_min_qty}+ items): {savingsPercent}% off
                                </span>
                                <span style={{ 
                                  color: theme.colors.textSecondary, 
                                  textDecoration: 'line-through',
                                  fontSize: '0.8rem'
                                }}>
                                  ${priceBeforeQuantityDiscount.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()
                    )}
                  </div>
                ) : authLoading ? (
                  <div style={{
                    padding: theme.spacing.md,
                    backgroundColor: theme.colors.surfaceAlt,
                    borderRadius: theme.borderRadius.md,
                    color: theme.colors.textSecondary,
                    display: 'inline-block',
                    fontSize: '0.9rem',
                  }}>
                    Loading pricing...
                  </div>
                ) : isAuthenticated && !isApproved ? (
                  <div style={{
                    padding: theme.spacing.md,
                    backgroundColor: `${theme.colors.cta}10`,
                    border: `1px solid ${theme.colors.cta}40`,
                    borderRadius: theme.borderRadius.md,
                    color: theme.colors.cta,
                    fontWeight: 600,
                    display: 'inline-block',
                  }}>
                    Account Approval Pending
                  </div>
                ) : (
                  <div style={{
                    padding: theme.spacing.md,
                    backgroundColor: theme.colors.surfaceAlt,
                    borderRadius: theme.borderRadius.md,
                    color: theme.colors.text,
                    display: 'inline-block',
                    fontSize: '0.9rem',
                  }}>
                    Log in for pricing
                  </div>
                )}
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: `1px solid ${theme.colors.border}`, margin: 0 }} />

            {/* Description */}
            {product.description && (
              <div>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  marginBottom: theme.spacing.sm,
                  color: theme.colors.text,
                }}>
                  Description
                </h3>
                <p style={{
                  color: theme.colors.textSecondary,
                  lineHeight: 1.6,
                  fontSize: '1rem',
                }}>
                  {product.description}
                </p>
              </div>
            )}

            {/* Variants/Subcategories */}
            {product.subcategories && product.subcategories.length > 0 && (
              <div style={{ marginBottom: theme.spacing.xl }}>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  marginBottom: theme.spacing.md,
                  color: theme.colors.text,
                }}>
                  Select Option
                </h3>
                
                <div className="variant-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: theme.spacing.md,
                }}>
                  {product.subcategories.map((subcategory) => {
                    const isSelected = selectedSubcategory?.id === subcategory.id;
                    const isOutOfStock = subcategory.stock_qty <= 0;
                    
                    return (
                      <div key={subcategory.id} style={{ position: 'relative' }}>
                        <button
                          onClick={() => !isOutOfStock && setSelectedSubcategory(subcategory)}
                          disabled={isOutOfStock}
                          style={{
                            border: isSelected ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius.lg,
                            padding: theme.spacing.sm,
                            cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            backgroundColor: isSelected ? `${theme.colors.primary}08` : theme.colors.surface,
                            opacity: isOutOfStock ? 0.6 : 1,
                            textAlign: 'left',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            width: '100%',
                            pointerEvents: isOutOfStock ? 'none' : 'auto',
                          }}
                        >
                        {/* Variant Image */}
                        <div style={{
                            width: '100%',
                            aspectRatio: '1',
                            marginBottom: theme.spacing.sm,
                            borderRadius: theme.borderRadius.md,
                            overflow: 'hidden',
                            backgroundColor: theme.colors.backgroundAlt,
                            position: 'relative',
                        }}>
                            {subcategory.images && subcategory.images.length > 0 ? (
                                <img
                                src={subcategory.images[0]}
                                alt={subcategory.name}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                }}
                                />
                            ) : (
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.colors.textSecondary,
                                    fontSize: '0.8rem'
                                }}>No Image</div>
                            )}
                            {isSelected && (
                                <div style={{
                                    position: 'absolute',
                                    top: '6px',
                                    right: '6px',
                                    backgroundColor: theme.colors.primary,
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}>✓</div>
                            )}
                        </div>
                        
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px', flexWrap: 'wrap' }}>
                              <p style={{
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                color: theme.colors.text,
                                margin: 0,
                                lineHeight: 1.3,
                                wordBreak: 'break-word',
                              }}>
                                {subcategory.name}
                              </p>
                              {/* Sale Discount Badge */}
                              {isAuthenticated && isSaleActive(subcategory.sale_discount_percent, subcategory.sale_start_date, subcategory.sale_end_date) 
                                && isSaleApplicableToTier(subcategory.sale_applicable_tiers) && (
                                <span style={{
                                  padding: '2px 4px',
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  borderRadius: '3px',
                                  fontSize: '0.6rem',
                                  fontWeight: '700',
                                  textTransform: 'uppercase',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {subcategory.sale_discount_percent}% OFF
                                </span>
                              )}
                              {/* Quantity Discount Badge */}
                              {isAuthenticated && subcategory.tier_prices?.quantity_discounts?.breaks?.length > 0 && (
                                (() => {
                                  // Check if any quantity discount applies to the current user's tier
                                  const hasApplicableDiscount = subcategory.tier_prices.quantity_discounts.breaks.some(
                                    breakItem => !breakItem.applicable_tiers || breakItem.applicable_tiers.length === 0 || breakItem.applicable_tiers.includes(pricingTier)
                                  );
                                  
                                  if (!hasApplicableDiscount) return null;
                                  
                                  // Find the minimum quantity for the first discount
                                  const minQty = Math.min(...subcategory.tier_prices.quantity_discounts.breaks.map(b => b.min_quantity));
                                  
                                  return (
                                    <span style={{
                                      padding: '2px 4px',
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      borderRadius: '3px',
                                      fontSize: '0.6rem',
                                      fontWeight: '700',
                                      textTransform: 'uppercase',
                                      whiteSpace: 'nowrap',
                                    }}>
                                      {minQty}+ DISC
                                    </span>
                                  );
                                })()
                              )}
                            </div>
                            {/* Type */}
                            {subcategory.type && (
                            <p style={{
                                fontSize: '0.75rem',
                                color: theme.colors.textSecondary,
                                marginBottom: '4px',
                            }}>
                                {subcategory.type}
                            </p>
                            )}
                            {subcategory.approx_sqft && (
                            <p style={{
                                fontSize: '0.75rem',
                                color: theme.colors.textSecondary,
                                margin: '0 0 4px',
                            }}>
                                About {formatSqft(subcategory.approx_sqft)} sq ft each
                            </p>
                            )}

                            {/* Pricing Display */}
                            {isAuthenticated && isApproved && (
                              <div style={{ marginTop: '4px' }}>
                                {(() => {
                                  // Get the appropriate tier price
                                  let displayPrice = null;
                                  
                                  if (subcategory.tier_prices) {
                                    const tierPrices = subcategory.tier_prices;
                                    if (pricingTier === 'volume' && tierPrices.volume) {
                                      displayPrice = parseFloat(tierPrices.volume);
                                    } else if (pricingTier === 'wholesale' && tierPrices.wholesale) {
                                      displayPrice = parseFloat(tierPrices.wholesale);
                                    } else if (tierPrices.retail) {
                                      displayPrice = parseFloat(tierPrices.retail);
                                    }
                                  } else if (subcategory.calculated_price !== undefined && subcategory.calculated_price !== null) {
                                    displayPrice = parseFloat(subcategory.calculated_price);
                                  }
                                  
                                  if (displayPrice === null) return null;
                                  
                                  // Check if there's an active sale
                                  const variantSaleActive = isSaleActive(
                                    subcategory.sale_discount_percent,
                                    subcategory.sale_start_date,
                                    subcategory.sale_end_date
                                  );
                                  const saleApplies = variantSaleActive && isSaleApplicableToTier(subcategory.sale_applicable_tiers);
                                  
                                  return (
                                    <div>
                                      <p style={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: saleApplies ? theme.colors.error : theme.colors.primary,
                                        margin: 0,
                                      }}>
                                        ${displayPrice.toFixed(2)}
                                      </p>
                                      {saleApplies && subcategory.tier_prices && (
                                        <p style={{
                                          fontSize: '0.75rem',
                                          color: theme.colors.textSecondary,
                                          margin: 0,
                                          textDecoration: 'line-through',
                                        }}>
                                          ${(() => {
                                            let basePrice = displayPrice / (1 - (subcategory.sale_discount_percent / 100));
                                            return basePrice.toFixed(2);
                                          })()}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            {!isAuthenticated && (
                              <p style={{
                                fontSize: '0.75rem',
                                color: theme.colors.textSecondary,
                                marginTop: '4px',
                                fontStyle: 'italic',
                              }}>
                                Log in for pricing
                              </p>
                            )}
                        </div>

                        {/* Stock Status */}
                        {(subcategory.stock_qty === 0 || subcategory.stock_qty < 10) && (
                            <div style={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: subcategory.stock_qty === 0 ? theme.colors.error : theme.colors.cta,
                                marginTop: '4px',
                            }}>
                                {subcategory.stock_qty === 0 ? 'Out of stock' : 'Low stock'}
                            </div>
                        )}
                      </button>
                      
                      {/* Notify bell button for out-of-stock variants - positioned outside disabled button */}
                      {isOutOfStock && isAuthenticated && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const tempVariant = subcategory;
                            setNotifyLoading(true);
                            api.stockNotifications.create({
                              product_id: productId,
                              variant_id: tempVariant.id,
                            })
                            .then(() => {
                              showSuccess(
                                `You'll be notified when "${product.name} - ${tempVariant.name}" is back in stock!`,
                                'Notification Set'
                              );
                            })
                            .catch((err) => {
                              console.error('Error creating stock notification:', err);
                              const errorMsg = err.response?.data?.detail || 'Failed to set notification';
                              showError(errorMsg, 'Notification Failed');
                            })
                            .finally(() => {
                              setNotifyLoading(false);
                            });
                          }}
                          disabled={notifyLoading}
                          title="Notify me when back in stock"
                          style={{
                            position: 'absolute',
                            top: '14px',
                            right: '14px',
                            width: '28px',
                            height: '28px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            color: theme.colors.primary,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: '50%',
                            cursor: notifyLoading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s ease',
                            zIndex: 10,
                            pointerEvents: 'auto',
                          }}
                          onMouseEnter={(e) => {
                            if (!notifyLoading) {
                              e.currentTarget.style.backgroundColor = theme.colors.primary;
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                            e.currentTarget.style.color = theme.colors.primary;
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Customizable Components */}
            {/* Customizable Components */}
            {product.components && product.components.length > 0 && (
              <div style={{ marginBottom: theme.spacing.xl }}>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  marginBottom: theme.spacing.md,
                  color: theme.colors.text,
                }}>
                  Customize Your Product
                </h3>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing.md,
                }}>
                  {product.components.map((component) => {
                    const selectedComp = selectedComponents.find(c => c.componentId === component.id);
                    const isSelected = !!selectedComp;
                    const hasVariations = component.variations && component.variations.length > 0;
                    const isActiveComponent = component.is_active;
                    
                    return (
                      <div
                        key={component.id}
                        onClick={() => {
                          if (!hasVariations && isActiveComponent) {
                            if (isSelected) {
                              setSelectedComponents(selectedComponents.filter(c => c.componentId !== component.id));
                            } else {
                              const componentPrice = component.price || 0;
                              setSelectedComponents([
                                ...selectedComponents,
                                {
                                  componentId: component.id,
                                  name: component.name,
                                  price: componentPrice,
                                  variationId: null,
                                  variationName: null,
                                }
                              ]);
                            }
                          }
                        }}
                        style={{
                          padding: theme.spacing.md,
                          borderRadius: theme.borderRadius.lg,
                          border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
                          backgroundColor: isSelected ? `${theme.colors.primary}05` : theme.colors.surface,
                          cursor: (!hasVariations && isActiveComponent) ? 'pointer' : 'default',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          opacity: isActiveComponent ? 1 : 0.6,
                        }}
                      >
                         {/* Header of component card */}
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <h4 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0, color: theme.colors.text }}>
                                    {component.name}
                                    {component.is_required && (
                                        <span style={{ 
                                            fontSize: '0.7rem', 
                                            color: theme.colors.error, 
                                            marginLeft: '8px',
                                            backgroundColor: `${theme.colors.error}10`,
                                            padding: '2px 6px',
                                            borderRadius: '4px'
                                        }}>
                                            REQUIRED
                                        </span>
                                    )}
                                </h4>
                                {component.description && (
                                    <p style={{ fontSize: '0.85rem', color: theme.colors.textSecondary, marginTop: '4px' }}>
                                        {component.description}
                                    </p>
                                )}
                            </div>
                            {isSelected && !hasVariations && (
                                <div style={{ color: theme.colors.primary, fontWeight: 700 }}>✓</div>
                            )}
                         </div>

                         {/* Variations */}
                         {hasVariations && (
                             <div style={{ marginTop: theme.spacing.md }}>
                                 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                                     {component.variations.map(variation => {
                                         const isVariationSelected = selectedComp?.variationId === variation.id;
                                         const isVarOutOfStock = variation.stock_qty <= 0;
                                         
                                         return (
                                           <div key={variation.id} style={{ position: 'relative' }}>
                                              <button
                                                  disabled={isVarOutOfStock}
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (isVariationSelected) {
                                                          setSelectedComponents(selectedComponents.filter(c => c.componentId !== component.id));
                                                      } else {
                                                          const newSelection = {
                                                              componentId: component.id,
                                                              variationId: variation.id,
                                                              name: component.name,
                                                              variationName: variation.name,
                                                              price: variation.price || component.price,
                                                          };
                                                          setSelectedComponents([
                                                              ...selectedComponents.filter(c => c.componentId !== component.id),
                                                              newSelection
                                                          ]);
                                                      }
                                                  }}
                                                  style={{
                                                      width: '100%',
                                                      padding: '8px',
                                                      borderRadius: theme.borderRadius.md,
                                                      border: `1px solid ${isVariationSelected ? theme.colors.primary : theme.colors.border}`,
                                                      backgroundColor: isVariationSelected ? theme.colors.primary : theme.colors.surface,
                                                      color: isVariationSelected ? 'white' : theme.colors.text,
                                                      cursor: isVarOutOfStock ? 'not-allowed' : 'pointer',
                                                      textAlign: 'center',
                                                      opacity: isVarOutOfStock ? 0.5 : 1,
                                                      transition: 'all 0.2s ease',
                                                      position: 'relative',
                                                      pointerEvents: isVarOutOfStock ? 'none' : 'auto',
                                                  }}
                                              >
                                                  {variation.images && variation.images.length > 0 ? (
                                                      <div style={{ position: 'relative' }}>
                                                        <img src={variation.images[0]} alt="" style={{ width: '100%', height: '60px', objectFit: 'cover', borderRadius: '4px', marginBottom: '4px' }} />
                                                      </div>
                                                  ) : null}
                                                  
                                                  <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{variation.name}</div>
                                                  {(product.price_visible || isApproved) && variation.price != null && (
                                                      <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>+${parseFloat(variation.price).toFixed(2)}</div>
                                                  )}
                                                  {isVarOutOfStock && (
                                                      <div style={{
                                                          fontSize: '0.7rem',
                                                          color: theme.colors.error,
                                                          fontWeight: 600,
                                                          marginTop: '4px',
                                                      }}>
                                                          Out of Stock
                                                      </div>
                                                  )}
                                              </button>
                                              
                                              {/* Notify bell button for out-of-stock component variations - positioned outside disabled button */}
                                              {isVarOutOfStock && isAuthenticated && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setNotifyLoading(true);
                                                    api.stockNotifications.create({
                                                      product_id: productId,
                                                      component_variation_id: variation.id,
                                                    })
                                                    .then(() => {
                                                      showSuccess(
                                                        `You'll be notified when "${component.name} - ${variation.name}" is back in stock!`,
                                                        'Notification Set'
                                                      );
                                                    })
                                                    .catch((err) => {
                                                      console.error('Error creating stock notification:', err);
                                                      const errorMsg = err.response?.data?.detail || 'Failed to set notification';
                                                      showError(errorMsg, 'Notification Failed');
                                                    })
                                                    .finally(() => {
                                                      setNotifyLoading(false);
                                                    });
                                                  }}
                                                  disabled={notifyLoading}
                                                  title="Notify me when back in stock"
                                                  style={{
                                                    position: 'absolute',
                                                    top: '12px',
                                                    right: '12px',
                                                    width: '24px',
                                                    height: '24px',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                    color: theme.colors.primary,
                                                    border: `1px solid ${theme.colors.border}`,
                                                    borderRadius: '50%',
                                                    cursor: notifyLoading ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    transition: 'all 0.2s ease',
                                                    zIndex: 10,
                                                    pointerEvents: 'auto',
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (!notifyLoading) {
                                                      e.currentTarget.style.backgroundColor = theme.colors.primary;
                                                      e.currentTarget.style.color = 'white';
                                                      e.currentTarget.style.transform = 'scale(1.1)';
                                                    }
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                                                    e.currentTarget.style.color = theme.colors.primary;
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                  }}
                                                >
                                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                                                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                                                  </svg>
                                                </button>
                                              )}
                                            </div>
                                         );
                                     })}
                                 </div>
                             </div>
                         )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Show total with components */}
                {selectedComponents.length > 0 && (product.price_visible || isApproved) && (
                  <div style={{
                    marginTop: theme.spacing.lg,
                    padding: theme.spacing.md,
                    backgroundColor: theme.colors.backgroundAlt,
                    borderRadius: theme.borderRadius.md,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: theme.colors.textSecondary,
                      }}>
                        Base: ${parseFloat(getCurrentPrice()).toFixed(2)} + Components: ${selectedComponents.reduce((sum, c) => sum + (parseFloat(c.price) || 0), 0).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div style={{
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        color: theme.colors.primary,
                      }}>
                        ${(parseFloat(getCurrentPrice()) + selectedComponents.reduce((sum, c) => sum + (parseFloat(c.price) || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Add to Cart Section */}
            {isDiscontinued ? (
                 <div role="status" style={{ marginTop: theme.spacing.xl, padding: theme.spacing.lg, textAlign: 'center', border: `1px dashed ${theme.colors.border}`, borderRadius: theme.borderRadius.md }}>
                    <p style={{ fontWeight: 600, marginBottom: '4px' }}>This item is no longer available</p>
                    <p style={{ fontSize: '0.9rem', color: theme.colors.textSecondary }}>It can’t be ordered anymore. Browse the catalog for similar pieces.</p>
                 </div>
            ) : !authLoading && isApproved ? (
              canShowAddToCart ? (
                <div className="add-to-cart-section" style={{
                  padding: theme.spacing.xl,
                  marginTop: theme.spacing.xl,
                  borderTop: `1px solid ${theme.colors.border}`,
                }}>
                  {/* Stock availability message */}
                  {availableStock > 0 && availableStock <= 20 && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: availableStock <= 5 ? theme.colors.error : theme.colors.cta,
                      marginBottom: theme.spacing.sm,
                      fontWeight: 600,
                    }}>
                      {availableStock <= 5 
                        ? `Only ${availableStock} available in stock` 
                        : `${availableStock} available`}
                    </div>
                  )}

                  {/* Discount Information */}
                  {(() => {
                     const priceBeforeQuantityDiscount = Number(priceWithSale) || 0;
                     const calculatedPriceNum = Number(calculatedPrice) || 0;
                     
                     // Check if quantity discount is strictly active (price difference exists AND discount percent > 0)
                     const hasQuantityDiscount = calculatedPrice !== null && 
                                                 calculatedPrice !== undefined && 
                                                 Math.abs(calculatedPriceNum - priceBeforeQuantityDiscount) > 0.01 &&
                                                 priceCalculation?.applied_discount_percent > 0;
                     
                     // Check if there's a next discount available (hint)
                     const nextDiscount = priceCalculation?.next_discount_min_qty
                       ? { minQty: priceCalculation.next_discount_min_qty, percent: priceCalculation.next_discount_percent } 
                       : null;
        
                     // Use activeSale directly, but also check if we have a sale active
                     const saleInfo = activeSale;
                     
                     if (!saleInfo && !hasQuantityDiscount && !nextDiscount) return null;
        
                     return (
                       <div style={{ 
                         marginBottom: theme.spacing.lg,
                         background: nextDiscount ? '#dbeafe' : '#ecfdf5', 
                         border: nextDiscount ? '1px solid #93c5fd' : '1px solid #6ee7b7',
                         color: nextDiscount ? '#2563eb' : '#047857',
                         padding: '10px 14px',
                         fontSize: '0.9rem',
                         fontWeight: 600,
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         gap: '8px',
                         borderRadius: theme.borderRadius.md,
                       }}>
                         {nextDiscount ? (
                            <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                              <span>Buy {nextDiscount.minQty} or more {nextDiscount.percent ? `for a ${nextDiscount.percent}% discount` : 'for a discount'}</span>
                            </>
                         ) : (
                            <>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                              {saleInfo && <span>{saleInfo.percent}% OFF Sale Active</span>}
                              {saleInfo && hasQuantityDiscount && <span>+</span>}
                              {hasQuantityDiscount && (
                                priceCalculation ? (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    Quantity Discount ({priceCalculation.applied_discount_min_qty}+ items): {priceCalculation.applied_discount_percent || ((priceBeforeQuantityDiscount - calculatedPriceNum) / priceBeforeQuantityDiscount * 100).toFixed(0)}% off
                                    <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>
                                      ${priceBeforeQuantityDiscount.toFixed(2)}
                                    </span>
                                  </span>
                                ) : (
                                  <span>Bulk Savings Applied</span>
                                )
                              )}
                            </>
                         )}
                       </div>
                     );
                  })()}
                  
                  {/* Quantity and Add Button Row */}
                  <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'stretch', flexWrap: 'wrap' }}>
                      {/* Quantity Input */}
                      <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: theme.spacing.xs,
                          flexShrink: 0,
                      }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}>
                            <label style={{
                              fontSize: theme.typography.fontSize.base,
                              color: theme.colors.text,
                              fontWeight: theme.typography.fontWeight.semibold,
                            }}>
                              Qty:
                            </label>
                            <input 
                              type="text"
                              inputMode="numeric"
                              placeholder={quantity.toString()}
                              value={quantityInput}
                              onChange={(e) => handleQuantityInputChange(e.target.value)}
                              onBlur={() => {
                                 if(pendingQuantity) handleCalculatePrice();
                              }}
                              style={{ 
                                width: '70px', 
                                textAlign: 'center', 
                                border: `2px solid ${theme.colors.border}`,
                                borderRadius: '12px',
                                padding: '12px',
                                fontSize: '1.25rem', 
                                fontWeight: 700, 
                                color: theme.colors.text,
                                backgroundColor: theme.colors.surface,
                                height: '56px',
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && pendingQuantity) {
                                  handleCalculatePrice();
                                  e.target.blur();
                                }
                              }}
                            />
                          </div>
                      </div>

                      {/* Add Button */}
                      <button
                        onClick={handleAddToCart}
                        disabled={isAddToCartDisabled}
                        style={{
                            flex: 1,
                            backgroundColor: isAddToCartDisabled ? theme.colors.border : theme.colors.text, // Modern black/dark button
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            cursor: isAddToCartDisabled ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px',
                            padding: '0 24px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s ease',
                            height: '56px',
                            opacity: isAddToCartDisabled ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!isAddToCartDisabled) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
                            e.currentTarget.style.backgroundColor = '#000'; // Slightly darker on hover if not already black
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isAddToCartDisabled) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.backgroundColor = theme.colors.text;
                          }
                        }}
                      >
                        {priceLoading ? (
                          <>
                            <span>Loading Price</span>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid rgba(255,255,255,0.3)',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                            }} />
                          </>
                        ) : !hasValidQuantity ? (
                          <span>Enter Quantity</span>
                        ) : (
                          <>
                            <span>Add to Cart</span>
                            {currentPrice && (
                                <span style={{ 
                                  opacity: 0.8, 
                                  fontSize: '0.9em',
                                  borderLeft: '1px solid rgba(255,255,255,0.3)',
                                  paddingLeft: '12px',
                                  fontWeight: 400
                                }}>
                                    {approxSqft ? 'Est. ' : ''}${(((Number(currentPrice) || 0) + selectedComponents.reduce((sum, sc) => sum + (parseFloat(sc.price) || 0), 0)) * (pendingQuantity || quantity)).toFixed(2)}
                                </span>
                            )}
                          </>
                        )}
                      </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: theme.spacing.xl, padding: theme.spacing.lg, backgroundColor: theme.colors.backgroundAlt, borderRadius: theme.borderRadius.md, textAlign: 'center' }}>
                    <p style={{ color: theme.colors.textSecondary, marginBottom: theme.spacing.md }}>
                      {hasSubcategories && !selectedSubcategory 
                        ? 'Please select a option above'
                        : availableStock === 0
                        ? 'This item is currently out of stock'
                        : !allRequiredComponentsSelected
                        ? `Please select all required components`
                        : !allComponentVariationsSelected
                        ? 'Please select a variation for all components'
                        : 'This item is currently unavailable'
                      }
                    </p>
                    
                    {/* Show "Notify Me" button when out of stock */}
                    {availableStock === 0 && (hasSubcategories ? selectedSubcategory : true) && isAuthenticated && (
                      <button
                        onClick={handleNotifyWhenInStock}
                        disabled={notifyLoading}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: theme.colors.primary,
                          color: 'white',
                          border: 'none',
                          borderRadius: theme.borderRadius.md,
                          cursor: notifyLoading ? 'not-allowed' : 'pointer',
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                        onMouseEnter={(e) => {
                          if (!notifyLoading) {
                            e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = theme.colors.primary;
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                        </svg>
                        {notifyLoading ? 'Setting up...' : 'Notify me when back in stock'}
                      </button>
                    )}
                 </div>
              )
            ) : authLoading ? (
                 <div style={{ marginTop: theme.spacing.xl, padding: theme.spacing.lg, textAlign: 'center', border: `1px dashed ${theme.colors.border}`, borderRadius: theme.borderRadius.md }}>
                    <p style={{ fontWeight: 600, marginBottom: '4px', color: theme.colors.textSecondary }}>Loading...</p>
                 </div>
            ) : !isApproved ? (
                 <div style={{ marginTop: theme.spacing.xl, padding: theme.spacing.lg, textAlign: 'center', border: `1px dashed ${theme.colors.border}`, borderRadius: theme.borderRadius.md }}>
                    <p style={{ fontWeight: 600, marginBottom: '4px' }}>Account Approval Required</p>
                    <p style={{ fontSize: '0.9rem', color: theme.colors.textSecondary }}>Your account needs to be approved before you can make purchases.</p>
                 </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Sticky Mobile Add to Cart */}
      {!authLoading && isApproved && canShowAddToCart && (
        <div className="mobile-cart-sticky" style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          padding: '12px 16px',
          borderTop: `1px solid ${theme.colors.border}`,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
          zIndex: 999, /* Increased z-index */
          display: 'none', // Controlled by CSS media query
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))', 
        }}>
          {/* Discount Banner */}
          {(() => {
             const priceBeforeQuantityDiscount = Number(priceWithSale) || 0;
             const calculatedPriceNum = Number(calculatedPrice) || 0;
             
             // Check if quantity discount is strictly active (price difference exists AND discount percent > 0)
             const hasQuantityDiscount = calculatedPrice !== null && 
                                         calculatedPrice !== undefined && 
                                         Math.abs(calculatedPriceNum - priceBeforeQuantityDiscount) > 0.01 &&
                                         priceCalculation?.applied_discount_percent > 0;
             
             // Check if there's a next discount available (hint)
             const nextDiscount = priceCalculation?.next_discount_min_qty 
               ? { minQty: priceCalculation.next_discount_min_qty, percent: priceCalculation.next_discount_percent } 
               : null;

             // Use activeSale directly, but also check if we have a sale active
             const saleInfo = activeSale;
             
             if (!saleInfo && !hasQuantityDiscount && !nextDiscount) return null;

             return (
               <div style={{ 
                 position: 'absolute', 
                 top: 0,
                 left: 0, 
                 right: 0, 
                 transform: 'translateY(-100%)', /* Push up outside the container */
                 background: nextDiscount ? '#dbeafe' : '#ecfdf5', 
                 borderTop: nextDiscount ? '1px solid #93c5fd' : '1px solid #6ee7b7',
                 color: nextDiscount ? '#2563eb' : '#047857',
                 padding: '8px 12px',
                 fontSize: '0.8rem',
                 fontWeight: 600,
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 gap: '6px',
                 boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                 zIndex: 1000,
                 width: '100%'
               }}>
                 {nextDiscount ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                      </svg>
                      <span>Buy {nextDiscount.minQty} or more {nextDiscount.percent ? `for a ${nextDiscount.percent}% discount` : 'for a discount'}</span>
                    </>
                 ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                              {saleInfo && <span>{saleInfo.percent}% OFF Sale Active</span>}
                              {saleInfo && hasQuantityDiscount && <span>+</span>}
                              {hasQuantityDiscount && (
                                priceCalculation ? (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    Quantity Discount ({priceCalculation.applied_discount_min_qty}+ items): {priceCalculation.applied_discount_percent || ((priceBeforeQuantityDiscount - calculatedPriceNum) / priceBeforeQuantityDiscount * 100).toFixed(0)}% off
                                    <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>
                                      ${priceBeforeQuantityDiscount.toFixed(2)}
                                    </span>
                                  </span>
                                ) : (
                                  <span>Bulk Savings Applied</span>
                                )
                              )}
                    </>
                 )}
               </div>
             );
          })()}

          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            width: '100%'
          }}>
             <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.colors.text }}>
                    ${(((Number(currentPrice) || 0) + selectedComponents.reduce((sum, sc) => sum + (parseFloat(sc.price) || 0), 0)) * (pendingQuantity || quantity)).toFixed(2)}
                  </span>
                  {(Number(activeSale ? baseTierPrice : priceWithSale) > Number(currentPrice)) && (
                     <span style={{ fontSize: '0.85rem', color: theme.colors.textSecondary, textDecoration: 'line-through' }}>
                        ${(((Number(activeSale ? baseTierPrice : priceWithSale) || 0) + selectedComponents.reduce((sum, sc) => sum + (parseFloat(sc.price) || 0), 0)) * (pendingQuantity || quantity)).toFixed(2)}
                     </span>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: theme.colors.textSecondary }}>
                   ${(Number(currentPrice) || 0).toFixed(2)} / unit
                </div>
             </div>

             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input 
                    type="text"
                    inputMode="numeric"
                    placeholder={quantity.toString()}
                    value={quantityInput}
                    onChange={(e) => handleQuantityInputChange(e.target.value)}
                    onBlur={() => {
                       if(pendingQuantity) handleCalculatePrice();
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && pendingQuantity) {
                        handleCalculatePrice();
                        e.target.blur();
                      }
                    }}
                    style={{
                       width: '60px',
                       height: '48px',
                       textAlign: 'center',
                       border: `1px solid ${theme.colors.border}`,
                       borderRadius: '8px',
                       fontWeight: 600,
                       fontSize: '1rem',
                       background: '#f9fafb',
                       color: theme.colors.text
                    }}
                  />
             </div>

             <button
                onClick={handleAddToCart}
                disabled={isAddToCartDisabled}
                style={{
                  flex: 1.5,
                  background: isAddToCartDisabled 
                    ? '#e5e7eb' 
                    : `linear-gradient(135deg, ${theme.colors.text} 0%, #000 100%)`,
                  color: isAddToCartDisabled ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  height: '48px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: isAddToCartDisabled ? 'not-allowed' : 'pointer',
                  boxShadow: isAddToCartDisabled ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
             >
                {priceLoading ? (
                   <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }} />
                ) : 'Add to Cart'}
             </button>
          </div>
        </div>
      )}

      {/* Image Lightbox Modal */}
      {lightboxOpen && displayImages && displayImages.length > 0 && (
        <div 
          className="lightbox-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeLightbox(); }}
        >
          {/* Top bar */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            zIndex: 10,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)',
          }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: 500 }}>
              {selectedImage + 1} / {displayImages.length}
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Zoom controls */}
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxZoom(prev => { const next = Math.max(prev - 0.5, 1); if (next === 1) setLightboxPan({ x: 0, y: 0 }); return next; }); }}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                  opacity: lightboxZoom <= 1 ? 0.3 : 1,
                  transition: 'all 0.2s ease',
                }}
                disabled={lightboxZoom <= 1}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </button>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', minWidth: '40px', textAlign: 'center' }}>
                {Math.round(lightboxZoom * 100)}%
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxZoom(prev => Math.min(prev + 0.5, 5)); }}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                  opacity: lightboxZoom >= 5 ? 0.3 : 1,
                  transition: 'all 0.2s ease',
                }}
                disabled={lightboxZoom >= 5}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </button>
              {/* Reset zoom */}
              {lightboxZoom > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxZoom(1); setLightboxPan({ x: 0, y: 0 }); }}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backdropFilter: 'blur(4px)',
                    transition: 'all 0.2s ease',
                  }}
                  title="Reset zoom"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                </button>
              )}
              {/* Close button */}
              <button
                onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                  marginLeft: '8px',
                  transition: 'all 0.2s ease',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>

          {/* Main lightbox image area */}
          <div
            className="lightbox-image-area"
            style={{
              flex: 1,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: lightboxZoom > 1 ? 'grab' : 'default',
              touchAction: 'none',
              userSelect: 'none',
            }}
            onWheel={handleLightboxWheel}
            onPointerDown={handleLightboxPointerDown}
            onPointerMove={handleLightboxPointerMove}
            onPointerUp={handleLightboxPointerUp}
            onTouchStart={handleLightboxTouchStart}
            onTouchMove={handleLightboxTouchMove}
            onTouchEnd={handleLightboxTouchEnd}
          >
            <img
              src={displayImages[selectedImage]}
              alt={selectedSubcategory ? `${product.name} - ${selectedSubcategory.name}` : product.name}
              draggable={false}
              style={{
                maxWidth: '90vw',
                maxHeight: 'calc(100vh - 160px)',
                objectFit: 'contain',
                transform: `scale(${lightboxZoom}) translate(${lightboxPan.x / lightboxZoom}px, ${lightboxPan.y / lightboxZoom}px)`,
                transition: isPanningRef.current ? 'none' : 'transform 0.2s ease',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          </div>

          {/* Navigation arrows */}
          {displayImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev > 0 ? prev - 1 : displayImages.length - 1); }}
                className="lightbox-nav-btn"
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.12)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                  transition: 'all 0.2s ease',
                  zIndex: 5,
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev < displayImages.length - 1 ? prev + 1 : 0); }}
                className="lightbox-nav-btn"
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.12)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                  transition: 'all 0.2s ease',
                  zIndex: 5,
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </>
          )}

          {/* Bottom thumbnail strip */}
          {displayImages.length > 1 && (
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '12px 20px',
              overflowX: 'auto',
              maxWidth: '100%',
              scrollbarWidth: 'none',
            }} className="lightbox-thumbnails">
              {displayImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setSelectedImage(idx); }}
                  style={{
                    width: '56px',
                    height: '56px',
                    minWidth: '56px',
                    border: `2px solid ${selectedImage === idx ? 'white' : 'transparent'}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    padding: '2px',
                    backgroundColor: selectedImage === idx ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
                    opacity: selectedImage === idx ? 1 : 0.5,
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={img}
                    alt={`View ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '6px',
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Styles */}
      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Thumbnail Gallery Scrollbar (Shared) */
        .thumbnail-gallery::-webkit-scrollbar,
        .desktop-thumbnails::-webkit-scrollbar {
          height: 6px;
          width: 6px;
        }
        
        .thumbnail-gallery::-webkit-scrollbar-track,
        .desktop-thumbnails::-webkit-scrollbar-track {
          background: ${theme.colors.border};
          border-radius: 3px;
        }
        
        .thumbnail-gallery::-webkit-scrollbar-thumb,
        .desktop-thumbnails::-webkit-scrollbar-thumb {
          background: ${theme.colors.primary};
          border-radius: 3px;
        }

        /* Hide arrows on mobile */
        @media (max-width: 768px) {
          .nav-arrow {
            display: none !important;
          }
          
          .breadcrumb {
            display: inline-block !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 200px;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 968px) {
          .product-detail-grid {
            grid-template-columns: 1fr !important;
            gap: ${theme.spacing.lg} !important;
          }

          .product-images-section {
            position: relative !important;
            top: 0 !important;
            margin-bottom: ${theme.spacing.lg};
          }
          
          /* Allow image to scale within square container */
          .main-image-container img {
            min-height: unset !important;
            max-height: unset !important;
            padding: ${theme.spacing.md} !important;
          }
          
          .variant-grid {
            grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)) !important;
            gap: ${theme.spacing.sm} !important;
          }

          .mobile-cart-sticky {
            display: flex !important;
            padding-bottom: max(20px, env(safe-area-inset-bottom)) !important;
            padding-top: 16px !important;
          }

          .add-to-cart-section {
            /* Hide the desktop add to cart section on mobile to avoid duplication */
            display: none !important; 
          }

          .product-header {
            padding: ${theme.spacing.sm} ${theme.spacing.md} !important;
          }
        }

        /* Extra small phones */
        @media (max-width: 400px) {
          .variant-grid {
            grid-template-columns: 1fr !important;
            gap: ${theme.spacing.sm} !important;
          }

          .variant-grid button {
            padding: ${theme.spacing.md} !important;
          }

          .variant-grid button > div:first-child {
            height: 140px !important;
          }

          .product-header {
            padding: ${theme.spacing.xs} ${theme.spacing.sm} !important;
          }

          .main-image-container img {
            padding: ${theme.spacing.md} !important;
          }
        }

        /* Tablet */
        @media (min-width: 969px) and (max-width: 1200px) {
          .product-detail-grid {
            gap: ${theme.spacing.xl} !important;
          }

          .main-image-container img {
            max-height: 600px !important;
          }
        }

        /* Desktop hover effects */
        @media (hover: hover) {
          .thumbnail-gallery button:hover,
          .desktop-thumbnails button:hover {
            opacity: 1 !important;
            transform: scale(1.05) !important;
          }

          .variant-grid button:not(:disabled):hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
        }

        /* Touch-friendly sizing */
        @media (hover: none) {
          button {
            min-height: 44px !important;
            min-width: 44px !important;
          }

          .thumbnail-gallery button {
            width: 70px !important;
            height: 70px !important;
          }

          .variant-grid button {
            padding: ${theme.spacing.sm} !important;
          }
        }

        /* Better variant spacing on small tablets */
        @media (min-width: 401px) and (max-width: 600px) {
          .variant-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: ${theme.spacing.md} !important;
          }
        }

        /* Medium tablets - 3 columns */
        @media (min-width: 601px) and (max-width: 968px) {
          .variant-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: ${theme.spacing.md} !important;
          }
        }

        /* Prevent text overflow in variant cards */
        .variant-grid button p {
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
        }

        /* Remove number input spinners */
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        input[type="number"] {
          -moz-appearance: textfield;
        }

        /* Lightbox styles */
        .lightbox-overlay {
          animation: lightboxFadeIn 0.2s ease;
        }

        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .lightbox-nav-btn:hover {
          background: rgba(255, 255, 255, 0.25) !important;
          transform: translateY(-50%) scale(1.1) !important;
        }

        .lightbox-thumbnails::-webkit-scrollbar {
          display: none;
        }

        .lightbox-thumbnails button:hover {
          opacity: 0.8 !important;
          background-color: rgba(255, 255, 255, 0.2) !important;
        }

        /* Zoom hint pulse on first load */
        .zoom-hint {
          animation: zoomHintPulse 2s ease-in-out 1s 1;
        }

        @keyframes zoomHintPulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }

        /* Main image hover zoom cursor */
        .main-image-container:hover .zoom-hint {
          opacity: 1 !important;
        }

        /* Desktop nav arrows show on main image hover */
        .main-image-container:hover .nav-arrow {
          opacity: 0.8 !important;
        }
        .nav-arrow:hover {
          opacity: 1 !important;
          transform: translateY(-50%) scale(1.1) !important;
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default ProductDetailPage;


